import { useState } from "react";

/**
 * The latest loaded `value`, for as long as the next query's is still loading or has failed.
 *
 * A template panel asks for more rows as the reader scrolls, and each request is a new query. A list
 * read from a store keyed by query is `undefined` until that query answers, so the panel emptied, and
 * the reader lost their place, every time it grew (#6862). RTK Query's own `data` keeps the last
 * answer in the same way; this does it for the stores that do not.
 *
 * `source` is the store's own record of the answer `value` was built from, such as the JSON a slice
 * keeps for the query. It is what says the answer changed. `value` cannot say it: a memoized selector
 * shared by two panels hands back a new object whenever the other panel reads it, and keeping each of
 * those would render again for ever. A refetch writes a new record, so a refreshed roster replaces the
 * one kept before it, and a later query's wait shows the refreshed roster, not the first (review of
 * #6896).
 */
export default function useRowsKeptWhileLoading<T>(value: T | undefined, source: unknown): T | undefined {
  const [last, setLast] = useState<{ source: unknown; value: T | undefined }>({ source, value });
  if (source !== undefined && value !== undefined && source !== last.source) setLast({ source, value });
  return source !== undefined && value !== undefined ? value : last.value;
}
